import { useAuth, useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { Check, Database, Edit2, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin-layout";

interface Season {
  id: number;
  season_name: string;
  start_month: number;
  start_day: number;
  end_month: number;
  end_day: number;
  price_multiplier: string;
  is_active: boolean;
}

interface Villa {
  id: number;
  villa_number: number;
  name: string;
  description: string | null;
  price_low_season: string | null;
  price_high_season: string | null;
  is_active: boolean;
}

export function AdminSettingsPage() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [villas, setVillas] = useState<Villa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingSampleData, setIsGeneratingSampleData] = useState(false);
  const [editingVilla, setEditingVilla] = useState<number | null>(null);
  const [villaFormData, setVillaFormData] = useState({
    name: "",
    description: "",
    price_low_season: "",
    price_high_season: "",
  });

  // Protect route - redirect if not authenticated
  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate("/login");
    }
  }, [user, isAuthLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [seasonsRes, villasRes] = await Promise.all([
        authenticatedFetch("/api/admin/seasons"),
        fetch("/api/villas"),
      ]);

      if (seasonsRes.status === 401) {
        navigate("/login");
        return;
      }

      if (seasonsRes.status === 403) {
        navigate("/unauthorized");
        return;
      }

      const [seasonsData, villasData] = await Promise.all([
        seasonsRes.json() as Promise<{ success: boolean; seasons: Season[] }>,
        villasRes.json() as Promise<{ success: boolean; villas: Villa[] }>,
      ]);

      if (seasonsData.success) {
        setSeasons(seasonsData.seasons);
      }

      if (villasData.success) {
        setVillas(villasData.villas);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeasonToggle = async (id: number, currentStatus: boolean) => {
    try {
      const response = await authenticatedFetch(`/api/admin/seasons/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !currentStatus }),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      if (response.ok) {
        toast.success("Season updated");
        fetchData();
      }
    } catch (error) {
      console.error("Failed to update season:", error);
      toast.error("Failed to update season");
    }
  };

  const handleVillaEdit = (villa: Villa) => {
    setEditingVilla(villa.id);
    setVillaFormData({
      name: villa.name,
      description: villa.description || "",
      price_low_season: villa.price_low_season || "250",
      price_high_season: villa.price_high_season || "350",
    });
  };

  const handleVillaSave = async (villaId: number) => {
    try {
      const response = await authenticatedFetch(`/api/admin/villas/${villaId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: villaFormData.name,
          description: villaFormData.description,
          price_low_season: parseFloat(villaFormData.price_low_season),
          price_high_season: parseFloat(villaFormData.price_high_season),
        }),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      if (response.ok) {
        toast.success("Villa updated");
        setEditingVilla(null);
        fetchData();
      }
    } catch (error) {
      console.error("Failed to update villa:", error);
      toast.error("Failed to update villa");
    }
  };

  const handleGenerateSampleData = async () => {
    setIsGeneratingSampleData(true);
    try {
      const response = await authenticatedFetch("/api/admin/sample-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (response.status === 403) {
        navigate("/unauthorized");
        return;
      }

      if (response.ok) {
        const data = (await response.json()) as { count: number; message: string };
        toast.success(`Created ${data.count} sample reservations for the next 30 days`);
      } else {
        const error = (await response.json()) as { message: string };
        toast.error(error.message || "Failed to generate sample data");
      }
    } catch (error) {
      console.error("Failed to generate sample data:", error);
      toast.error("Failed to generate sample data");
    } finally {
      setIsGeneratingSampleData(false);
    }
  };

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1).toLocaleString("default", { month: "short" });
  };

  // Show loading spinner while checking authentication
  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!user) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Libre_Baskerville'] text-3xl font-bold text-stone-900 mb-1">
            Settings
          </h1>
          <p className="text-stone-600">Manage pricing, seasons, and villa details</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full size-8 border-b-2 border-stone-900" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Demo Data */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
              <h2 className="font-semibold text-xl mb-3 flex items-center gap-2">
                <Database className="size-5 text-amber-700" />
                Demo Data
              </h2>
              <p className="text-sm text-amber-900 mb-4">
                Generate sample reservations for the next 30 days. This helps test the booking
                calendar and admin dashboard with realistic data.
              </p>
              <button
                onClick={handleGenerateSampleData}
                disabled={isGeneratingSampleData}
                className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-500 disabled:cursor-not-allowed text-white px-6 py-3 rounded font-semibold transition-colors"
              >
                {isGeneratingSampleData ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Database className="size-4" />
                    Load Sample Data (+30 days)
                  </>
                )}
              </button>
            </div>

            {/* Seasonal Pricing */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h2 className="font-semibold text-xl mb-4">Seasonal Pricing</h2>
              <p className="text-sm text-stone-600 mb-6">
                Manage high and low season date ranges. High season rates are automatically applied
                during these periods.
              </p>

              <div className="space-y-3">
                {seasons.map((season) => (
                  <div
                    key={season.id}
                    className="flex items-center justify-between p-4 border border-stone-200 rounded"
                  >
                    <div className="flex-1">
                      <div className="font-semibold text-stone-900">{season.season_name}</div>
                      <div className="text-sm text-stone-600">
                        {getMonthName(season.start_month)} {season.start_day} -{" "}
                        {getMonthName(season.end_month)} {season.end_day}
                      </div>
                      <div className="text-xs text-stone-500 mt-1">
                        {((parseFloat(season.price_multiplier) - 1) * 100).toFixed(0)}% price
                        increase
                      </div>
                    </div>

                    <button
                      onClick={() => handleSeasonToggle(season.id, season.is_active)}
                      className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                        season.is_active
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                    >
                      {season.is_active ? "Active" : "Inactive"}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Villa Management */}
            <div className="bg-white border border-stone-200 rounded-lg p-6">
              <h2 className="font-semibold text-xl mb-4">Villa Management</h2>
              <p className="text-sm text-stone-600 mb-6">
                Update villa details and pricing. Changes will be reflected immediately on the
                booking pages.
              </p>

              <div className="space-y-4">
                {villas.map((villa) => (
                  <div key={villa.id} className="border border-stone-200 rounded-lg p-4">
                    {editingVilla === villa.id ? (
                      /* Edit mode */
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2">Villa Name</label>
                          <input
                            type="text"
                            value={villaFormData.name}
                            onChange={(e) =>
                              setVillaFormData({ ...villaFormData, name: e.target.value })
                            }
                            className="w-full border border-stone-300 px-3 py-2 rounded focus:outline-none focus:border-stone-900"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-2">Description</label>
                          <textarea
                            value={villaFormData.description}
                            onChange={(e) =>
                              setVillaFormData({ ...villaFormData, description: e.target.value })
                            }
                            rows={3}
                            className="w-full border border-stone-300 px-3 py-2 rounded focus:outline-none focus:border-stone-900 resize-none"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-2">
                              Low Season Rate (USD)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={villaFormData.price_low_season}
                              onChange={(e) =>
                                setVillaFormData({
                                  ...villaFormData,
                                  price_low_season: e.target.value,
                                })
                              }
                              className="w-full border border-stone-300 px-3 py-2 rounded focus:outline-none focus:border-stone-900"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold mb-2">
                              High Season Rate (USD)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={villaFormData.price_high_season}
                              onChange={(e) =>
                                setVillaFormData({
                                  ...villaFormData,
                                  price_high_season: e.target.value,
                                })
                              }
                              className="w-full border border-stone-300 px-3 py-2 rounded focus:outline-none focus:border-stone-900"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleVillaSave(villa.id)}
                            className="flex items-center gap-2 bg-stone-900 text-white px-4 py-2 rounded hover:bg-stone-800 transition-colors"
                          >
                            <Check className="size-4" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingVilla(null)}
                            className="flex items-center gap-2 bg-stone-100 text-stone-700 px-4 py-2 rounded hover:bg-stone-200 transition-colors"
                          >
                            <X className="size-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View mode */
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{villa.name}</h3>
                          <p className="text-sm text-stone-600 mt-1">
                            {villa.description || "No description"}
                          </p>
                          <div className="flex gap-4 mt-3 text-sm">
                            <div>
                              <span className="text-stone-500">Low Season:</span>{" "}
                              <span className="font-semibold">${villa.price_low_season}/night</span>
                            </div>
                            <div>
                              <span className="text-stone-500">High Season:</span>{" "}
                              <span className="font-semibold">
                                ${villa.price_high_season}/night
                              </span>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleVillaEdit(villa)}
                          className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
                        >
                          <Edit2 className="size-4" />
                          <span className="text-sm">Edit</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
